import type { DivergenceRow } from "../api/rest";

/**
 * Aging (shelf-life) report helpers (ADR 194). Age comes from the WMS FIFO
 * date against the snapshot's own asOf — computed backend-side on the
 * divergence rows, so map, table and chart all read ONE number. A scan
 * timestamp only says when the camera saw a bin; it is never an age source.
 *
 * Buckets count WMS line items (address, product), not units: unit quantities
 * of different SKUs are not comparable, line items are what ops walks to.
 */

export interface AgeBucket {
  key: string;
  /** Upper bound in days, inclusive; Infinity for the open tail. */
  maxDays: number;
  /** Line items per reconciliation outcome — found (match), qty divergence,
   *  missing. unexpected_in_ic3 never lands here (it has no WMS side). */
  match: number;
  qtyMismatch: number;
  missing: number;
}

export const AGE_BUCKET_BOUNDS: ReadonlyArray<{ key: string; maxDays: number }> = [
  { key: "d30", maxDays: 30 },
  { key: "d90", maxDays: 90 },
  { key: "d180", maxDays: 180 },
  { key: "d365", maxDays: 365 },
  { key: "over", maxDays: Infinity },
];

/** Rows with an age, tallied into the fixed buckets by outcome. A negative age
 *  (FIFO after asOf — WMS data noise) classifies into the first bucket; the
 *  raw value stays visible wherever the row itself renders. */
export function ageBuckets(rows: DivergenceRow[]): AgeBucket[] {
  const buckets: AgeBucket[] = AGE_BUCKET_BOUNDS.map((b) => ({ ...b, match: 0, qtyMismatch: 0, missing: 0 }));
  for (const r of rows) {
    if (r.ageDays === null) continue;
    const age = Math.max(0, r.ageDays);
    const b = buckets.find((x) => age <= x.maxDays)!;
    if (r.status === "match") b.match++;
    else if (r.status === "qty_mismatch") b.qtyMismatch++;
    else if (r.status === "missing_in_ic3") b.missing++;
  }
  return buckets;
}

export interface ExpiryBucket {
  key: "expired" | "d30" | "d90" | "d180" | "over";
  /** Upper bound in days to expiry, inclusive; Infinity for the open tail. */
  maxDays: number;
  /** WMS line items (match, qty divergence, missing) whose expiry falls in the bucket. */
  items: number;
}

export const EXPIRY_BUCKET_BOUNDS: ReadonlyArray<{ key: ExpiryBucket["key"]; maxDays: number }> = [
  { key: "expired", maxDays: -1 },
  { key: "d30", maxDays: 30 },
  { key: "d90", maxDays: 90 },
  { key: "d180", maxDays: 180 },
  { key: "over", maxDays: Infinity },
];

/** ADR 410: rows with a WMS expiry, tallied by days left at the snapshot's asOf.
 *  Negative days are "expired". Rows without an expiry are skipped, never zero. */
export function expiryBuckets(rows: DivergenceRow[]): ExpiryBucket[] {
  const buckets: ExpiryBucket[] = EXPIRY_BUCKET_BOUNDS.map((b) => ({ ...b, items: 0 }));
  for (const r of rows) {
    if (r.daysToExpiry == null) continue;
    if (r.status !== "match" && r.status !== "qty_mismatch" && r.status !== "missing_in_ic3") continue;
    buckets.find((b) => r.daysToExpiry! <= b.maxDays)!.items++;
  }
  return buckets;
}

/** The oldest stock on the WMS side, descending by age (ties by address so the
 *  order is stable), capped at `limit` for the report table. */
export function oldestStock(rows: DivergenceRow[], limit = 15): DivergenceRow[] {
  return rows
    .filter((r) => r.ageDays !== null)
    .sort((a, b) => (b.ageDays! - a.ageDays!) || a.address.localeCompare(b.address))
    .slice(0, limit);
}

/** Whether the snapshot carries aging at all — gates the whole section. */
export function hasAging(rows: DivergenceRow[]): boolean {
  return rows.some((r) => r.ageDays !== null);
}
